// Systems: the rules that run over a whole room rather than over one object.
//
// Moved out of GameScene (2026-07-30, sim extraction step 3b/3c). Water flow,
// conduit gates, rune altars and ambient decay each read the room's current state
// and update it; none of them now touch a sprite. What they visibly did is
// reported as SimEvents instead (see events.ts).
//
// They are no longer invoked by hand from each site that can invalidate them:
// `settle.ts` declares which system depends on which and runs them in that order
// (2026-07-31, Phase 1), and mutation sites call `settleWorld` instead of
// choosing systems for themselves. What is left here is each system's own rule.
// Nothing in this file calls another system directly -- ordering lives in exactly
// one place now.
import { RoomDef } from './types';
import { SimEvents } from './events';
import { DEFAULT_PHYSICS, PhysicsConfig, countedDelta } from './physics';
import { ConceptId } from '../runes/concepts';
import { CasterTile, gateIsOpen, gateShowsOpen, isConduitCharged, isConditionMet, isSluice, isWrecked, traceWaterFlow } from './queries';
import { addObjectToRoom, checkMeltThreshold, removeObjectFromRoom } from './mechanics';
import { decayVines } from './vines';
import { FLUID_FLOWING, FLUID_SOLID, heatTarget } from './heatState';
import { bindInvolvedObjects } from './thermal';

const tileKey = (x: number, y: number) => `${x},${y}`;

/** The four orthogonal neighbours, for "has fluid reached the far side of this
 * shut bridge". Orthogonal because that is the only way the trace steps (see
 * `traceWaterFlow`), so a diagonal neighbour running is water that was never
 * coming here. */
const DAM_OFFSETS: readonly (readonly [number, number])[] = [
  [0, -1],
  [1, 0],
  [0, 1],
  [-1, 0],
];

// room8's pad-gates and room9's pillar-gates: every trigger wired to a gate
// (see GateTriggerDef) fires on the rising edge of its own conditions all
// reading satisfied at once, and what firing does -- open, close or toggle --
// is the trigger's to say. Run by `settleWorld` after any world mutation, last
// in the declared order -- never from removeObjectFromRoom itself, since
// that's what this calls, and gates have no conditions of their own to
// re-check.
//
// Only the rising edge fires, and only a trigger that says so ever shuts a
// gate: carrying the rock back off its pad still cannot undo an `open`
// trigger, which is what both original gate puzzles' second halves depend on.
// The state itself lives on the gate (`gate.open`) rather than being
// re-derived, so a gate that opened stays open across a room re-entry, when
// every sprite is rebuilt from scratch.
//
// `caster` is where the player currently stands, for the one condition that
// reads it (a `pad.forPlayer` pressure pad). Optional so a test or a tool can
// still check a bare room's gates; `settleWorld` always passes it.
export function checkGateThreshold(room: RoomDef, out: SimEvents, physics: PhysicsConfig = DEFAULT_PHYSICS, caster?: CasterTile): boolean {
  // Whether a **sluice** changed state -- the one thing this pass does that an
  // earlier system read. See `settle.ts`: a room with a sluice in it settles in
  // two passes, because the water flow was computed before this ran.
  let sluiceMoved = false;
  // A conduit pillar's lit/unlit look tracks isConduitCharged live. Emitted on
  // every gate check, not only when it flips -- see events.ts on why the
  // renderer, not the sim, is what suppresses the repeat.
  for (const pillar of room.objects.filter((o) => o.conduit?.feed)) {
    if (!pillar.litTexture) continue;
    const charged = isConduitCharged(room, pillar, physics);
    out.push({
      kind: 'texture-set',
      objectId: pillar.id,
      texture: charged ? pillar.litTexture : pillar.texture,
      // A one-shot activation pop on the unlit->lit transition specifically.
      // The reverse (lit->unlit) just goes dark -- losing charge doesn't need
      // its own flourish the way gaining it does.
      transition: charged ? 'pop' : 'instant',
    });
  }

  for (const obj of [...room.objects]) {
    const gate = obj.gate;
    if (!gate) continue;
    const wasOpen = gateIsOpen(gate);
    let open = wasOpen;

    for (const trigger of gate.triggers ?? []) {
      // A trigger naming an id nothing in the room answers to reads as
      // unsatisfied rather than as vacuously true -- the loader refuses a room
      // whose triggers dangle, so the only way to get here is an object that
      // was removed at runtime, and "the thing holding this open is gone"
      // should not read as "the condition is met".
      const conditions = trigger.conditions ?? [];
      const met = conditions.length > 0 && conditions.every((c) => isConditionMet(room, c, physics, caster));
      if (trigger.mode === 'hold' && trigger.action !== 'toggle') {
        // Live re-derivation, not edge-triggered: this trigger's own condition
        // drives `open` every settle, in whichever direction its own action
        // means -- 'close' holds the gate shut exactly while met, 'open' (the
        // default) holds it open exactly while met. No `satisfied` guard, on
        // purpose: falling out of `met` un-derives it on the very next settle,
        // unlike every other trigger here. `toggle` is excluded because "flip
        // continuously while held" is not a sensible ask -- see GateTriggerMode.
        open = trigger.action === 'close' ? !met : met;
      } else if (met && !trigger.satisfied) {
        // The whole reason `satisfied` is persisted on the trigger: this system
        // runs after every mutation, so "is it satisfied" alone would re-fire a
        // toggle on every push while the rock sat on the pad.
        open = trigger.action === 'close' ? false : trigger.action === 'toggle' ? !open : true;
      }
      // Written only when it actually changes, `!!` included: an unfired
      // trigger keeps no `satisfied` key at all rather than gaining a
      // `satisfied: false` the first time any settle runs. What that buys is
      // that a room the editor exports, or a recording that embeds one, still
      // matches what was authored -- runtime bookkeeping that means "nothing
      // has happened yet" should not show up as authored content.
      if (met !== !!trigger.satisfied) trigger.satisfied = met;
    }

    // Same rule for the gate's own state: a gate that has never opened carries
    // no `open` key, and `gateIsOpen` falls through to `defaultOpen`.
    if (open !== wasOpen) gate.open = open;
    if (open !== wasOpen && isSluice(obj)) sluiceMoved = true;
    const texture = open ? (gate.openTexture ?? obj.texture) : obj.texture;

    // A sluice's look is its gate state crossed with whether fluid has reached
    // it, and the second half is a fact about the room rather than about the
    // object -- so `recomputeWaterFlow` paints it, exactly as `settleConveyors`
    // rather than `settleRust` paints a belt (see `rust.ts`'s `ownsItsOwnLook`
    // for the same argument at length). Painting here as well is not a
    // duplicate, it is the *other* half of the cross product being asserted by a
    // pass that cannot see the flow: whichever ran last would win, and an open
    // sluice with water running through it would go back to being drawn dry.
    if (isSluice(obj)) {
      if (open !== wasOpen) {
        if (open) out.push({ kind: 'puzzle-solved', objectId: obj.id });
        out.push({ kind: 'interact-changed', objectId: obj.id });
      }
      continue;
    }

    if (open === wasOpen) {
      // Nothing moved. A gate that can be seen open still restates its texture
      // every check, the same way the conduit-pillar loop above does and for
      // the same reason: sprites are built from `obj.texture` on every room
      // entry, so a gate left standing open needs saying again on the other
      // side of a door. The renderer, not the sim, suppresses the repeat.
      if (gateShowsOpen(gate)) out.push({ kind: 'texture-set', objectId: obj.id, texture, transition: 'instant' });
      continue;
    }

    if (open && !gateShowsOpen(gate)) {
      // Nothing can ever shut this one and it started closed, so there is no
      // open state for anyone to see -- it leaves the room outright, which is
      // what every gate did before triggers existed.
      out.push({ kind: 'puzzle-solved', objectId: obj.id });
      removeObjectFromRoom(room, obj, out);
      continue;
    }

    // Passability follows from `gate.open` live (see isWalkable), so all that
    // is left is saying so: the texture, and the fact that what the player is
    // facing may now read differently.
    if (open) out.push({ kind: 'puzzle-solved', objectId: obj.id });
    out.push({ kind: 'texture-set', objectId: obj.id, texture, transition: 'cross-fade' });
    out.push({ kind: 'interact-changed', objectId: obj.id });
  }
  return sluiceMoved;
}

// Recomputes which tiles currently carry flowing water -- run by `settleWorld`
// after anything that could change the channel's topology (a Transfer touching
// the aqueduct or a riverbed tile's Energy, or a push moving an obstacle in or
// out of the channel) without each of those sites having to know that. Also the
// single place "water reaching a wheel powers a reservoir"
// (water.powersReservoir) is checked, so that stays a generic side effect
// of the flow recompute rather than its own bespoke trigger.
//
// Returns the flowing tile set, which the caller keeps: it blocks *player*
// movement without blocking a pushed object from being shoved onto it, which is
// the entire redirect mechanic. Always a fresh set, including on the early
// return for rooms with no water system at all, so a stale set from room9 can
// never linger and block tiles in an unrelated room reusing the same
// coordinates.
export function recomputeWaterFlow(room: RoomDef, out: SimEvents): Set<string> {
  // One trace per fluid that actually has a source here, unioned (2026-08-09).
  // The union is what every caller wants -- "is a fluid running over this tile
  // right now" is the question the push-vacate check, the editor overlay and
  // the freeze gate all ask, and none of them care which one. Fluid identity
  // matters inside the trace (a channel carries one kind) and in the freeze
  // thresholds, and that is where it is read.
  const fluids = [...new Set(room.objects.filter((o) => o.water?.source).map((o) => o.water!.fluid ?? 'water'))];
  // No early return for a room with no source, since 2026-08-18. There used to
  // be one, and it was safe while this pass only painted textures over tiles
  // that were already showing their authored look. Now that the same pass owns
  // the name and the Inspect text too, bailing here would leave a tile in a
  // sourceless room stuck in whatever look it last had -- so the trace is simply
  // empty and every tile paints dry, which is the honest answer and costs one
  // loop over a room whose objects nearly all `continue` on the first line.
  const flowing = new Set<string>();
  for (const fluid of fluids) for (const tile of traceWaterFlow(room, fluid)) flowing.add(tile);

  for (const obj of room.objects) {
    const water = obj.water;
    // A standing body has no `flowingTexture` -- its own `texture` is what full
    // looks like -- but it still has a frozen look to swap to, so it is painted
    // here alongside the channels (2026-08-09). A **bridge** joined them on
    // 2026-08-11: room9's Aqueduct is a trough, and a trough is dry until
    // something runs through it. It was drawn brim-full of water from the
    // instant it was mended, whether or not the channel feeding it reached it
    // -- reported as "the aqueduct should not show water unless water is
    // flowing", and true of it in room16, where the same restored aqueduct sits
    // on a spring whose flow a single frozen tile upstream can cut.
    if (!water?.flowingTexture && !water?.body && !water?.dammedTexture) continue;
    // What this object looks like with no fluid on it. Its authored `texture`,
    // except while a `transformsInto` toggle is showing its other face:
    // `resolveBarrier` deliberately never writes that one back (`obj.texture`
    // is one of the two states it toggles between), so the transformed look has
    // to be read off the flag rather than off the field.
    // A **sluice** has a second thing deciding its dry look: the gate in it. Its
    // four states are the two facts crossed -- shut/open against dry/wet -- and
    // this is the one pass that can see both, which is why `checkGateThreshold`
    // deliberately does not paint one.
    const open = obj.gate ? gateIsOpen(obj.gate) : false;
    const base = obj.barrier?.transformed && obj.barrier.transformsInto ? obj.barrier.transformsInto : obj.gate && open ? (obj.gate.openTexture ?? obj.texture) : obj.texture;
    // Real, persisted state (see WaterDef.frozen) -- checkWaterFreeze
    // (heatState.ts, called via checkMeltThreshold) is what actually
    // watches the freeze/melt threshold pair and flips this; this loop only
    // ever reads the result, the same way it only ever reads reachability.
    const frozen = !!water.frozen;
    // **Fluid has arrived at a shut bridge** -- water standing against a closed
    // sluice. Asked of the *neighbouring* tiles rather than its own, and it has
    // to be: a shut bridge is not water-passable, so it is never in the trace,
    // so its own tile is dry by construction however much water is piled
    // against it. Which is the whole picture the fourth sprite draws.
    const dammed = !!water.dammedTexture && !open && DAM_OFFSETS.some(([dx, dy]) => flowing.has(tileKey(obj.x + dx, obj.y + dy)));
    // **A wrecked machine never shows its working look.** A seized Water Wheel
    // with the river hitting it is not a turning wheel, and the same reading
    // that stops it driving its reservoir stops it drawing as though it were.
    // `isWrecked` rather than `meltRulesSatisfied`, because the Aqueduct in the
    // same channel satisfies its own rule exactly when it is *mended*.
    const working = !isWrecked(obj);
    const look = frozen && water.frozenTexture ? 'solid' : working && water.flowingTexture && flowing.has(tileKey(obj.x, obj.y)) ? 'flowing' : dammed ? 'dammed' : 'dry';
    const desired = look === 'solid' ? water.frozenTexture! : look === 'flowing' ? water.flowingTexture! : look === 'dammed' ? water.dammedTexture! : base;
    out.push({
      kind: 'texture-set',
      objectId: obj.id,
      texture: desired,
      transition: look === 'flowing' ? 'fade-in' : 'instant',
    });

    // The other two thirds of the same presentation, decided by the same branch
    // (2026-08-18). They were split across two systems before: this pass owned
    // the texture and `checkWaterFreeze` owned the name and the Inspect text, so
    // a tile the river had been redirected into swapped its sprite and went on
    // reading "a dry channel -- nothing moves through it now".
    //
    // The wet looks are laws (`FLUID_SOLID`, `FLUID_FLOWING`) and the dry look
    // is authored per object, so the authored pair is recorded the first time
    // this paints anything else and handed straight back on the way down --
    // `barrier.intact`'s record-and-restore, for `barrier.intact`'s reason.
    const fluid = water.fluid ?? 'water';
    const solid = look === 'solid' ? FLUID_SOLID[fluid] : undefined;
    const wet = look === 'flowing' ? (water.flowingInteract ?? { label: obj.interact?.label ?? 'Inspect', text: FLUID_FLOWING[fluid].description }) : undefined;
    if (solid || wet) water.authored ??= { name: obj.name, interact: obj.interact };
    const wasName = obj.name;
    const wasText = obj.interact?.text;
    if (solid) {
      obj.name = solid.name;
      obj.interact = { ...obj.interact, label: obj.interact?.label ?? 'Inspect', text: solid.description };
    } else if (wet) {
      // The name is left alone: a wet riverbed is the same riverbed with water
      // in it, where ice is genuinely a different substance. See FLUID_FLOWING.
      obj.name = water.authored!.name;
      obj.interact = wet;
    } else if (water.authored) {
      obj.name = water.authored.name;
      obj.interact = water.authored.interact;
      water.authored = undefined;
    }
    if (obj.name !== wasName || obj.interact?.text !== wasText) out.push({ kind: 'interact-changed', objectId: obj.id });
  }

  // Water reaching a machine drives it -- unless the machine is broken.
  //
  // `isWrecked` is the same live reading `resolveBarrier` collapses on,
  // asked here rather than a persisted "is it rubble" flag, so the two can never
  // disagree about whether the thing is working: a Water Wheel at or above its
  // Entropy threshold is a seized wheel with a river running past it, and drains
  // its reservoir back to nothing rather than leaving a stale reading behind.
  // Drain rather than skip, because the reservoir holds no state of its own that
  // says where its Charge came from -- skipping would leave the last good
  // reading standing and the Substation would go on powering the Tower off a
  // wheel that had stopped turning.
  for (const obj of room.objects) {
    const powers = obj.water?.powersReservoir;
    if (!powers) continue;
    const driven = flowing.has(tileKey(obj.x, obj.y)) && !isWrecked(obj);
    const reservoir = room.objects.find((o) => o.id === powers.id);
    if (!reservoir?.properties) continue;
    reservoir.properties[powers.concept] = driven ? powers.amount : 0;
  }

  // The gate re-check that used to end this function is now the 'conduit-gates'
  // system running immediately after this one -- same order, declared once in
  // settle.ts instead of hidden in the tail of the system it depends on.
  return flowing;
}

// room10's Rune Circle -> Portal swap: generic and data-driven (keyed off
// isRuneAltar/activatesInto), the same way checkGateThreshold is driven by
// gatePadIds/gatePillarIds rather than a hardcoded per-room check, even though
// only room10 uses either field today. A no-op until every isRuneAltar object
// sharing the room has altarFilled -- which is why it can be a declared system
// run after every mutation (see settle.ts) rather than something the altar-
// placement site has to remember to call: the only thing that can set
// altarFilled is a placement, and once the swap has happened the dormant object
// it looks for is gone.
export function checkRuneAltars(room: RoomDef, out: SimEvents): void {
  const altars = room.objects.filter((o) => o.altar);
  if (altars.length === 0 || !altars.every((a) => a.altar!.filled)) return;
  const dormant = room.objects.find((o) => o.activatesInto);
  if (!dormant) return;
  out.push({ kind: 'puzzle-solved', objectId: dormant.id });
  removeObjectFromRoom(room, dormant, out);
  addObjectToRoom(room, dormant.activatesInto!, out);
  // The gate re-check that used to end this function (a runtime object can land
  // straight on a pressure pad) is now the 'conduit-gates' system, which
  // settle.ts declares as running after this one.
}

// Environmental Energy decay toward the room's own baseline (Newton's law of
// cooling), plus reservoir regeneration back toward maxProperties. One tick's
// worth; the caller decides how often a tick happens.
export function applyRoomDecay(room: RoomDef, out: SimEvents, physics: PhysicsConfig = DEFAULT_PHYSICS, flowingTiles?: ReadonlySet<string>): void {
  // physics.player.thermalStart, not a bare 0 -- see RoomDef.ambientEnergy's
  // own doc comment on why an unset room now defaults to neutral rather than
  // cold, now that this same baseline also drives the caster's own thermalState.
  const ambient = room.ambientEnergy ?? physics.player.thermalStart;
  const perTick = physics.room.decayPerTick;
  // Either side of a live Bind link -- Energy or Charge -- gets its own
  // ambient/regen edge from solveThermalStep (thermal.ts) instead, computed
  // together with its Bind adjustment from the same tick's snapshot -- skip
  // them here, or this loop would double-apply against that. See
  // bindInvolvedObjects' own doc comment.
  //
  // The maxProperties branch below is the one that has to stay in step:
  // thermal.ts reproduces it only for the concepts it actually solves
  // (SOLVED_CONCEPTS -- Energy and Charge), so a bind-involved object with a
  // maxProperties entry for anything else would silently stop regenerating.
  // Nothing in the authored world has one; sim.test.ts pins that, so it fails
  // there rather than as a reservoir that quietly went flat.
  const skip = new Set(bindInvolvedObjects(room, flowingTiles).map((o) => o.id));
  for (const obj of [...room.objects]) {
    // Guards only the three branches solveThermalStep also covers
    // (meltsWhen/heatState/maxProperties) -- decayVines below is
    // deliberately outside this guard; thermal.ts's own module comment
    // explains why a bind-linked vine (nothing shipped does this) isn't
    // handled there.
    if (!skip.has(obj.id)) {
      // barrier.transformsInto objects used to be exempted from this branch
      // entirely (an Energy-driven one, like room9's Bonfire used to be,
      // would otherwise light from a cast and immediately un-light itself
      // the next decay tick, since nothing kept it above the same threshold
      // it had just crossed). That exemption is gone as of 2026-08-06: the
      // Bonfire moved onto HeatStateDef below, which solves the same problem
      // for real (a Source's own bias keeps its equilibrium clear of its own
      // extinguish threshold, rather than freezing decay altogether). Nothing
      // else is both Energy-driven and transformsInto -- room6/room9's other
      // transformsInto pairs (the Standard/Reliquary, the Aqueduct) all watch
      // Entropy, which never decays here at all, so they were never affected
      // by this branch either way.
      if (obj.properties?.energy !== undefined && obj.barrier?.meltsWhen && !obj.heatState) {
        obj.properties.energy += countedDelta(ambient - obj.properties.energy, perTick);
        checkMeltThreshold(room, obj, out, physics);
      }
      // A HeatStateDef object's target isn't bare ambient -- it's ambient plus
      // whatever its *current* state's own bias is (see heatState.ts). checkMeltThreshold
      // (called via the same shared entry point as every other branch here)
      // is what actually notices a crossing and swaps state.
      if (obj.heatState) {
        const concept = obj.heatState.concept;
        const current = obj.properties?.[concept] ?? 0;
        const target = heatTarget(obj, ambient);
        obj.properties = obj.properties ?? {};
        obj.properties[concept] = current + countedDelta(target - current, perTick);
        checkMeltThreshold(room, obj, out, physics);
      }
      if (obj.properties && obj.maxProperties) {
        for (const [concept, max] of Object.entries(obj.maxProperties) as [ConceptId, number][]) {
          // A state ladder owns the equilibrium of the concept it governs, and
          // `maxProperties` is only its ceiling. Running both would mean an
          // extinguished hearth climbing straight back to full and relighting
          // itself the next tick -- the regen edge pulling toward the cap while
          // the ladder pulls toward zero, with the cap always winning because it
          // is the larger number. So "put it out" would have meant nothing.
          if (obj.heatState?.concept === concept) continue;
          // Charge is not regenerated per object any more (2026-08-08): a
          // source's rated output is its *network's* equilibrium, and the
          // network relaxes toward it in both directions rather than each
          // source climbing back to its own ceiling while injected Charge
          // elsewhere sits banked forever. See chargeBind.ts's
          // relaxChargeNetworks, called from tick() alongside this.
          if (concept === 'charge') continue;
          const current = obj.properties[concept] ?? 0;
          obj.properties[concept] = current + countedDelta(max - current, perTick);
        }
      }
    }
    // Vines never use barrier.meltsWhen (see sim/vines.ts on why the state
    // ladder is its own module rather than another meltsWhen rule), so the
    // branch above was never going to reach them -- decayVines is the same
    // "Energy drifts toward ambient" rule plus the one thing unique to
    // vines, Entropy accruing while sustained heat keeps them burning.
    if (obj.vines) decayVines(obj, ambient, physics, out);
  }
}
